import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useStores } from "@/stores";
import type { TablesInsert } from "@/types/supabase-helpers";

// Simple category types for document categories (Image or Text based)
const SIMPLE_CATEGORY_TYPES = [
  { value: "image", label: "Image" },
  { value: "text", label: "Text" },
];

interface CreateCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateCategoryDialog({ open, onOpenChange }: CreateCategoryDialogProps) {
  const { documentCategoriesStore } = useStores();
  const [categoryName, setCategoryName] = useState("");
  const [categoryType, setCategoryType] = useState("");
  const [description, setDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const resetForm = () => {
    setCategoryName("");
    setCategoryType("");
    setDescription("");
  };

  const handleCreate = async () => {
    if (!categoryName.trim()) {
      toast.error("Category name is required");
      return;
    }

    if (!categoryType) {
      toast.error("Category type is required");
      return;
    }

    setIsCreating(true);
    try {
      const newCategory: TablesInsert<"document_categories"> = {
        category_name: categoryName.trim(),
        category_type: categoryType,
        description: description.trim() || null,
      };

      await documentCategoriesStore.createCategory(newCategory);
      toast.success("Category created successfully");
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to create category");
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isCreating) {
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Category</DialogTitle>
          <DialogDescription>Add a new document category for classification.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="category_name">Category Name *</Label>
            <Input
              id="category_name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g., Building Permit"
              disabled={isCreating}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="category_type">Category Type *</Label>
            <Select value={categoryType} onValueChange={setCategoryType} disabled={isCreating}>
              <SelectTrigger id="category_type">
                <SelectValue placeholder="Select a type..." />
              </SelectTrigger>
              <SelectContent>
                {SIMPLE_CATEGORY_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              className="min-h-[80px]"
              disabled={isCreating}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Category"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
